import type {
  BroadcastMessage,
  ClientMetadata,
  InboundMessageRequest,
  InboundMessageResponse,
  ServerEvent,
  WidgetConfig,
} from '@massimo/widget-protocol'

import { renderWidget } from './widget'

/**
 * Entry point pro <script src=".../widget.js" data-org="dogpoint"></script> embed.
 *
 * Strategy:
 *   1. Číst config z data-* atributů na <script> tagu (nebo window.MassimoWidget queue)
 *   2. Inject shadow DOM root (zero CSS leak z host page)
 *   3. Render widget UI
 *   4. Připojit handlery: send message (POST), receive stream (SSE)
 */

declare global {
  interface Window {
    MassimoWidget?: WidgetController
  }
}

interface WidgetController {
  open(): void
  close(): void
  /** Přepne open↔close (PLA-15 — host page menu „Podpora" hook). */
  toggle(): void
  /** Aktuální stav panelu (true=open). Public API pro host page (PLA-15). */
  isOpen(): boolean
  /**
   * Runtime update visitor identity + custom metadata. Merguje per pole na
   * existing snapshot (z `data-*`). Hostitelská appka by měla volat až má
   * authenticated user info (např. po loginu).
   */
  identify(identity: {
    email?: string
    displayName?: string
    metadata?: Partial<ClientMetadata>
  }): void
}

function getCurrentScript(): HTMLScriptElement | null {
  // ES modules NEMAJÍ document.currentScript (HTML spec) — vždy fallback.
  // Hledáme přes typické cesty: widget.js (prod IIFE) nebo embed.ts (Vite dev).
  // Plus jakýkoliv <script> s data-org atributem (defenzivní).
  const fromCurrent = document.currentScript as HTMLScriptElement | null
  if (fromCurrent?.dataset.org) return fromCurrent

  const scripts = document.getElementsByTagName('script')
  for (let i = 0; i < scripts.length; i++) {
    const s = scripts[i]
    if (!s) continue
    if (s.dataset.org) return s
    if (s.src.includes('widget.js') || s.src.includes('embed.ts')) return s
  }
  return null
}

function readConfigFromScript(): WidgetConfig {
  const script = getCurrentScript()
  const orgSlug = script?.dataset.org ?? ''
  const baseUrl = script?.dataset.baseUrl ?? deriveBaseUrl(script?.src ?? 'http://localhost:3030')
  return {
    orgSlug,
    baseUrl,
    primaryColor: script?.dataset.color,
    position: (script?.dataset.position as WidgetConfig['position']) ?? 'bottom-right',
    locale: (script?.dataset.locale as WidgetConfig['locale']) ?? 'cs',
    theme: (script?.dataset.theme as WidgetConfig['theme']) ?? 'light',
  }
}

/**
 * Lehký User-Agent parser — extract browser + os name. Nepoužíváme UA-CH
 * (modern API) kvůli backward compat; klasický UA string je dost na MVP.
 * NE-pixel-perfect detekce, jen "Chrome / macOS" pro agent context.
 */
function parseUserAgent(ua: string): { browser?: string; os?: string } {
  const out: { browser?: string; os?: string } = {}
  if (/Edg\//.test(ua)) out.browser = 'Edge'
  else if (/Chrome\//.test(ua)) out.browser = 'Chrome'
  else if (/Firefox\//.test(ua)) out.browser = 'Firefox'
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) out.browser = 'Safari'

  if (/Windows NT/.test(ua)) out.os = 'Windows'
  else if (/Mac OS X/.test(ua) || /Macintosh/.test(ua)) out.os = 'macOS'
  else if (/Android/.test(ua)) out.os = 'Android'
  else if (/(iPhone|iPad|iOS)/.test(ua)) out.os = 'iOS'
  else if (/Linux/.test(ua)) out.os = 'Linux'
  return out
}

/**
 * Číst well-known `data-*` atributy + `data-custom-metadata` (JSON) ze snippetu
 * → `ClientMetadata` (per spec § 5.1). Auto-collect (`language`, `environment`,
 * `resolution`) doplníme — auto-collected pole jsou override-able přes data-*.
 * `sourceUrl` JE per-message — viz `currentSourceUrl()`.
 */
function readClientMetadataFromScript(): ClientMetadata {
  const script = getCurrentScript()
  const meta: ClientMetadata = {}
  if (!script) return meta

  // Identity (well-known)
  const ds = script.dataset
  if (ds.email) meta.email = ds.email
  if (ds.uuid) meta.uuid = ds.uuid
  if (ds.name) meta.name = ds.name
  if (ds.phone) meta.phone = ds.phone
  if (ds.companyId && ds.companyName) {
    meta.company = { id: ds.companyId, name: ds.companyName }
  }

  // Language: data-language preferred, jinak navigator.language
  const lang = ds.language || navigator.language
  if (lang) meta.language = lang

  // Custom metadata JSON
  if (ds.customMetadata) {
    try {
      const parsed = JSON.parse(ds.customMetadata)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        meta.customMetadata = parsed as Record<string, unknown>
      }
    } catch {
      console.warn('[MassimoWidget] data-custom-metadata není validní JSON, ignoruji')
    }
  }

  // Auto-collect: environment + resolution
  const ua = navigator.userAgent
  meta.environment = { ...parseUserAgent(ua), userAgent: ua.slice(0, 512) }
  if (window.innerWidth && window.innerHeight) {
    meta.resolution = { w: window.innerWidth, h: window.innerHeight }
  }

  return meta
}

/** Per-message page URL — re-čte při každém send (visitor může proklikat). */
function currentSourceUrl(): string {
  return location.href
}

function deriveBaseUrl(scriptSrc: string): string {
  // Heuristika: pokud script je hostlý jako http://localhost:3040/widget.js,
  // API endpoint je na :3030 (shell). V produkci by se přepisovalo na env.
  try {
    const u = new URL(scriptSrc)
    if (u.hostname === 'localhost' && u.port === '3040') {
      return 'http://localhost:3030'
    }
    return u.origin
  } catch {
    return 'http://localhost:3030'
  }
}

const SESSION_KEY = 'massimo_session_id'
function getOrCreateSessionId(): string {
  try {
    const existing = localStorage.getItem(SESSION_KEY)
    if (existing) return existing
    const fresh = crypto.randomUUID()
    localStorage.setItem(SESSION_KEY, fresh)
    return fresh
  } catch {
    // Storage blocked (incognito strict) → ephemeral session
    return crypto.randomUUID()
  }
}

let identity: { sessionId: string; email?: string; displayName?: string; locale?: string } = {
  sessionId: getOrCreateSessionId(),
  locale: navigator.language?.split('-')[0],
}

/**
 * Client metadata snapshot — base z `data-*` snippetu, doplnitelné runtime
 * přes `MassimoWidget.identify({ metadata })` (merge per pole). Posíláme:
 *   - vždy v session POST (init/hydrate)
 *   - vždy ve **first** message (BE zapíše snapshot na thread)
 *   - v subsequent message volitelně (jen pokud klient app mezitím updatne)
 * BE merguje idempotentně.
 */
let clientMetadata: ClientMetadata = {}

let threadId: string | null = null
let eventSource: EventSource | null = null

function nonEmpty(m: ClientMetadata): ClientMetadata | undefined {
  return Object.keys(m).length > 0 ? m : undefined
}

interface SessionHydration {
  threadId: string | null
  messages: BroadcastMessage[]
}

async function hydrateSession(config: WidgetConfig): Promise<SessionHydration> {
  try {
    const res = await fetch(`${config.baseUrl}/api/widget/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        orgSlug: config.orgSlug,
        sessionId: identity.sessionId,
        clientMetadata: nonEmpty(clientMetadata),
      }),
    })
    const data = (await res.json()) as {
      ok: boolean
      threadId?: string
      messages?: BroadcastMessage[]
    }
    if (data.ok && data.threadId) {
      threadId = data.threadId
      connectStream(config)
      return { threadId: data.threadId, messages: data.messages ?? [] }
    }
  } catch {
    /* offline — widget bude start clean, žádný error pro visitora */
  }
  return { threadId: null, messages: [] }
}

async function sendTyping(isTyping: boolean, config: WidgetConfig): Promise<void> {
  if (!threadId) return
  try {
    await fetch(`${config.baseUrl}/api/widget/typing`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ threadId, isTyping }),
    })
  } catch {
    /* typing je ephemeral, fail silent */
  }
}

async function sendMessage(
  body: string,
  config: WidgetConfig,
): Promise<{ ok: boolean; error?: string; messageId?: string }> {
  const payload: InboundMessageRequest = {
    orgSlug: config.orgSlug,
    identity,
    body,
    context: {
      pageUrl: location.href,
      pageTitle: document.title,
      referrer: document.referrer || undefined,
      userAgent: navigator.userAgent,
    },
    sourceUrl: currentSourceUrl(),
    ...(nonEmpty(clientMetadata) ? { clientMetadata: { ...clientMetadata } } : {}),
  }
  const res = await fetch(`${config.baseUrl}/api/widget/message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const data = (await res.json()) as InboundMessageResponse
  if (data.ok && data.threadId && !threadId) {
    threadId = data.threadId
    connectStream(config)
  }
  return { ok: data.ok, error: data.error, messageId: data.messageId }
}

function connectStream(config: WidgetConfig): void {
  if (!threadId || eventSource) return
  eventSource = new EventSource(`${config.baseUrl}/api/widget/stream/${threadId}`)
  eventSource.onmessage = (e) => {
    try {
      const event = JSON.parse(e.data) as ServerEvent
      onServerEvent(event)
    } catch {
      /* invalid payload, ignore */
    }
  }
  eventSource.onerror = () => {
    // Auto-reconnect handled by EventSource itself
  }
}

let onMessageHandler: ((m: BroadcastMessage) => void) | null = null
let onStatusHandler: ((status: 'open' | 'pending' | 'resolved' | 'closed') => void) | null = null
let onAgentTypingHandler: ((isTyping: boolean) => void) | null = null

function onServerEvent(event: ServerEvent): void {
  switch (event.type) {
    case 'message.created':
      // Skip echoes our own inbound (visitor already sees own message optimistically)
      if (event.message.kind === 'inbound') return
      onMessageHandler?.(event.message)
      break
    case 'thread.status_changed':
      onStatusHandler?.(event.status)
      break
    case 'typing':
      // Visitor's own typing echo — skip. Agent typing → bubble pro visitora.
      if (event.who === 'agent') onAgentTypingHandler?.(event.isTyping)
      break
    case 'connected':
    case 'heartbeat':
      /* no-op */
      break
  }
}

async function fetchTenantConfig(config: WidgetConfig): Promise<Partial<WidgetConfig> | null> {
  // Fetchneme JEN BRAND (title/subtitle/primaryColor). Behavior (locale/position/theme)
  // je per-deployment v data-* attributes — server ne­zná.
  //
  // Visitor locale detection: config.locale je z `data-locale` v snippetu;
  // pokud 'auto' nebo chybí, fallback na navigator.language.
  const visitorLocale =
    config.locale && config.locale !== 'auto'
      ? config.locale
      : navigator.language?.split('-')[0] === 'en'
        ? 'en'
        : 'cs'

  try {
    const url = `${config.baseUrl}/api/widget/config?org=${encodeURIComponent(config.orgSlug)}&locale=${visitorLocale}`
    const res = await fetch(url)
    // 403 = Origin allowlist check failed → caller (init) skip render úplně.
    if (res.status === 403) {
      console.warn(
        '[MassimoWidget] This origin is not in the tenant allowed_domains list — widget disabled.',
      )
      return null
    }
    if (!res.ok) return {}
    const data = (await res.json()) as {
      ok: boolean
      title?: string
      subtitle?: string
      primaryColor?: string | null
      officeStatus?: 'online' | 'offline' | null
      officeStatusMessage?: string | null
    }
    if (!data.ok) return {}
    return {
      title: data.title || undefined,
      subtitle: data.subtitle || undefined,
      primaryColor: data.primaryColor || undefined,
      officeStatus: data.officeStatus ?? undefined,
      officeStatusMessage: data.officeStatusMessage ?? undefined,
      // locale rezolvovaný pro renderWidget (TEXTS lookup) — z visitor preference
      locale: (visitorLocale === 'en' ? 'en' : 'cs') as 'cs' | 'en',
    }
  } catch {
    return {}
  }
}

async function init(): Promise<void> {
  const baseConfig = readConfigFromScript()
  if (!baseConfig.orgSlug) {
    console.warn('[MassimoWidget] Chybí data-org atribut na <script> tagu')
    return
  }

  // Fetch tenant config (title/subtitle/color) server-side resolved per visitor locale.
  // Pokud endpoint nedostupný, widget se renderne s defaults — graceful degradation.
  // Pokud Origin není v allowedDomains tenanta (server vrátil 403 + null), skip
  // render úplně — anti brand-abuse.
  const tenantOverrides = await fetchTenantConfig(baseConfig)
  if (tenantOverrides === null) return // origin-blocked
  const config: WidgetConfig = { ...baseConfig, ...tenantOverrides }

  // Base client metadata z `data-*` snippetu + auto-collect (language/environment/resolution).
  // `MassimoWidget.identify({ metadata })` ji pak může runtime rozšířit/přepsat (per-pole merge).
  clientMetadata = readClientMetadataFromScript()

  const controller = renderWidget({
    config,
    onSend: (body) => sendMessage(body, config),
    onTyping: (isTyping) => sendTyping(isTyping, config),
    onMessageReceived: (handler) => {
      onMessageHandler = handler
    },
    onStatusChanged: (handler) => {
      onStatusHandler = handler
    },
    onAgentTyping: (handler) => {
      onAgentTypingHandler = handler
    },
  })

  // Async hydrate — pokud visitor má sessionId v localStorage a existuje thread,
  // naložíme historii do widgetu (bez čekání na klik na bubble).
  void hydrateSession(config).then(({ messages }) => {
    if (messages.length > 0) {
      controller.hydrate(messages)
    }
  })

  window.MassimoWidget = {
    open: controller.open,
    close: controller.close,
    toggle: controller.toggle,
    isOpen: controller.isOpen,
    identify: (id) => {
      const { metadata: incomingMeta, ...rest } = id
      identity = { ...identity, ...rest }
      if (incomingMeta) {
        // Per-pole merge na strukturovaný snapshot (well-known fieldy si držíme
        // přesně, customMetadata merguje na key úroveň).
        if (incomingMeta.email !== undefined) clientMetadata.email = incomingMeta.email
        if (incomingMeta.uuid !== undefined) clientMetadata.uuid = incomingMeta.uuid
        if (incomingMeta.name !== undefined) clientMetadata.name = incomingMeta.name
        if (incomingMeta.phone !== undefined) clientMetadata.phone = incomingMeta.phone
        if (incomingMeta.company !== undefined) clientMetadata.company = incomingMeta.company
        if (incomingMeta.language !== undefined) clientMetadata.language = incomingMeta.language
        if (incomingMeta.customMetadata) {
          clientMetadata.customMetadata = {
            ...(clientMetadata.customMetadata ?? {}),
            ...incomingMeta.customMetadata,
          }
        }
      }
    },
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    void init()
  })
} else {
  void init()
}
